import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, Power, AlertCircle, CheckCircle, Lock, Unlock } from 'lucide-react';
import apiClient from '../../utils/apiClient';

interface FeatureFlag {
    id: number;
    feature_key: string;
    enabled: boolean;
    hidden: boolean;
    description: string;
    enabled_by: string | null;
    enabled_at: string | null;
}

const FeatureFlagsManagement: React.FC = () => {
    const [flags, setFlags] = useState<FeatureFlag[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showHidden, setShowHidden] = useState(false);
    const [roleLevel, setRoleLevel] = useState(3); // 開発者権限
    const [magicCode, setMagicCode] = useState('');
    const [magicMessage, setMagicMessage] = useState<string | null>(null);

    useEffect(() => {
        fetchFlags();
    }, [showHidden]);


    const fetchFlags = async () => {
        try {
            setLoading(true);
            // 開発者専用ページなので /all エンドポイントを使用
            const endpoint = showHidden ? '/feature-flags/all' : '/feature-flags/visible';

            const response = await apiClient.get(endpoint);
            setFlags(response.data.flags || []);
            setError(null);
        } catch (err: any) {
            // 401エラーの場合は認証が必要
            if (err.response?.status === 401) {
                setError('認証が必要です。開発者でログインしてください。');
            } else {
                setError(err instanceof Error ? err.message : '機能フラグの取得に失敗しました');
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleEnabled = async (featureKey: string, currentEnabled: boolean) => {
        try {
            await apiClient.put(`/feature-flags/toggle/${featureKey}`, {
                enabled: !currentEnabled
            });
            await fetchFlags();
        } catch (err) {
            alert(err instanceof Error ? err.message : '更新に失敗しました');
        }
    };

    const toggleVisibility = async (featureKey: string, currentHidden: boolean) => {
        if (roleLevel < 3) {
            alert('開発者権限が必要です');
            return;
        }

        try {
            await apiClient.put(`/feature-flags/visibility/${featureKey}`, {
                hidden: !currentHidden
            });
            await fetchFlags();
        } catch (err) {
            alert(err instanceof Error ? err.message : '更新に失敗しました');
        }
    };

    const handleMagicCode = async () => {
        try {
            const response = await apiClient.post('/feature-flags/magic', {
                code: magicCode
            });

            if (response.data) {
                setMagicMessage(response.data.message || '✅ コード実行成功');
                setMagicCode('');
                await fetchFlags();
                setTimeout(() => setMagicMessage(null), 3000);
            }
        } catch (err) {
            setMagicMessage('❌ 無効なコード');
            setTimeout(() => setMagicMessage(null), 3000);
        }
    };

    const getFeatureName = (key: string): string => {
        const names: Record<string, string> = {
            visit_stamp: '来店スタンプ',
            birthday_bonus: '誕生日特典',
            feedback_survey: '食後アンケート',
            coupon_notification: 'クーポン通知',
            member_card_hub: 'メンバーカードHub',
            member_card_v2: 'メンバーカードv2（6タブ）',
            continuous_visit_bonus: '連続来店ボーナス',
            advanced_analytics: '高度な分析機能'
        };
        return names[key] || key;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">読み込み中...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center text-red-800">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg">
                {/* Header */}
                <div className="border-b border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <Shield className="w-8 h-8 text-indigo-600 mr-3" />
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">権限項目管理</h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    機能のON/OFF切り替えと表示制御
                                </p>
                            </div>
                        </div>

                        {roleLevel >= 3 && (
                            <button
                                onClick={() => setShowHidden(!showHidden)}
                                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                                    showHidden
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {showHidden ? <Eye className="w-5 h-5 mr-2" /> : <EyeOff className="w-5 h-5 mr-2" />}
                                {showHidden ? '権限項目を表示中' : '権限項目を表示'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Feature Flags List */}
                <div className="p-6">
                    <div className="space-y-4">
                        {flags.map((flag) => (
                            <div
                                key={flag.id}
                                className={`border rounded-lg p-4 transition-all ${
                                    flag.hidden && roleLevel >= 3
                                        ? 'border-dashed border-gray-300 bg-gray-50'
                                        : 'border-gray-200'
                                }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                {getFeatureName(flag.feature_key)}
                                            </h3>
                                            {flag.hidden && roleLevel >= 3 && (
                                                <span className="ml-3 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                                    非表示
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                            機能キー: <code className="bg-gray-100 px-2 py-0.5 rounded">{flag.feature_key}</code>
                                        </p>
                                        {flag.enabled_by && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                有効化: {flag.enabled_by} ({new Date(flag.enabled_at!).toLocaleString('ja-JP')})
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        {/* 有効/無効ボタン */}
                                        <button
                                            onClick={() => toggleEnabled(flag.feature_key, flag.enabled)}
                                            className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all ${
                                                flag.enabled
                                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                                            }`}
                                        >
                                            {flag.enabled ? (
                                                <>
                                                    <Unlock className="w-4 h-4 mr-2" />
                                                    有効
                                                </>
                                            ) : (
                                                <>
                                                    <Lock className="w-4 h-4 mr-2" />
                                                    無効
                                                </>
                                            )}
                                        </button>

                                        {/* 表示/非表示ボタン（開発者のみ） */}
                                        {roleLevel >= 3 && (
                                            <button
                                                onClick={() => toggleVisibility(flag.feature_key, flag.hidden)}
                                                className={`p-2 rounded-lg transition-colors ${
                                                    flag.hidden
                                                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                }`}
                                                title={flag.hidden ? '管理画面に表示する' : '管理画面から非表示にする'}
                                            >
                                                {flag.hidden ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {flags.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            機能フラグが登録されていません
                        </div>
                    )}
                </div>

                {/* Magic Code Section */}
                <div className="border-t border-gray-200 p-6 bg-gray-50">
                    <div className="max-w-md mx-auto">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">🔮 マジックコード</h3>
                        <div className="flex space-x-2">
                            <input
                                type="text"
                                value={magicCode}
                                onChange={(e) => setMagicCode(e.target.value)}
                                placeholder="WATON2025SPECIAL"
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                onKeyPress={(e) => e.key === 'Enter' && handleMagicCode()}
                            />
                            <button
                                onClick={handleMagicCode}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                実行
                            </button>
                        </div>
                        {magicMessage && (
                            <p className="mt-2 text-sm text-center text-gray-600">{magicMessage}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FeatureFlagsManagement;
